import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { HoFeesShareApi, type SchoolFeesSummaryRow } from '@/services/ho-fees-share.api';
import { PageHeader } from '@/components/ui/PageHeader';
import { Select, Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { fmtMoney } from '@/pages/fees/lib/feeFormat';
import { useDataTable, DataTablePagination } from '@/components/data-table';

function SchoolClassDetailsModal({ school, sessionYear, onClose }: { school: SchoolFeesSummaryRow; sessionYear: string; onClose: () => void }) {
  const { data: classRows = [], isFetching } = useQuery({
    queryKey: ['org-school-fees-class-summary', school.schoolId, sessionYear],
    queryFn: () => HoFeesShareApi.classSummaryForOrg(school.schoolId, sessionYear),
  });
  const [printingClass, setPrintingClass] = useState<string | null>(null);

  const handlePrint = async (className: string) => {
    setPrintingClass(className);
    try {
      await HoFeesShareApi.printClassFeeDemand(school.schoolId, sessionYear, className);
    } finally {
      setPrintingClass(null);
    }
  };

  return (
    <Modal open onClose={onClose} title={`${school.schoolName} — Fee Details`} description={`Session year ${sessionYear}`} size="lg">
      <div className="overflow-hidden rounded-lg border border-slate-200">
        <table className="min-w-full divide-y divide-slate-200 text-sm">
          <thead className="bg-slate-50">
            <tr>
              <th className="px-4 py-2 text-left font-medium text-slate-600">No.</th>
              <th className="px-4 py-2 text-left font-medium text-slate-600">Class</th>
              <th className="px-4 py-2 text-left font-medium text-slate-600">Students</th>
              <th className="px-4 py-2 text-left font-medium text-slate-600">Total Fee</th>
              <th className="px-4 py-2 text-left font-medium text-slate-600">Paid Fee</th>
              <th className="px-4 py-2 text-left font-medium text-slate-600">Due Fee</th>
              <th className="px-4 py-2 text-left font-medium text-slate-600">Print</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {classRows.map((r, i) => (
              <tr key={r.className} className="hover:bg-slate-50">
                <td className="px-4 py-2 text-slate-400">{i + 1}</td>
                <td className="px-4 py-2 font-medium text-slate-800">{r.className}</td>
                <td className="px-4 py-2 text-slate-600">{r.studentCount}</td>
                <td className="px-4 py-2 text-slate-600">{fmtMoney(r.totalFee)}</td>
                <td className="px-4 py-2 text-green-600">{fmtMoney(r.paidFee)}</td>
                <td className="px-4 py-2 text-amber-600">{fmtMoney(r.dueFee)}</td>
                <td className="px-4 py-2">
                  <button
                    type="button"
                    className="text-sm font-medium text-blue-600 hover:underline disabled:opacity-50"
                    disabled={printingClass === r.className}
                    onClick={() => handlePrint(r.className)}
                  >
                    {printingClass === r.className ? 'Preparing…' : 'Print'}
                  </button>
                </td>
              </tr>
            ))}
            {!isFetching && classRows.length === 0 && (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-sm text-slate-400">
                  No class-wise fee data for this session year.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </Modal>
  );
}

/** Org tier — "School Summary". Student-facing total/paid/due fees per school, by session year — a DIFFERENT metric from Head Office Fees' 30%-share reconciliation (see backend doc comment). */
export function OrgSchoolFeesSummaryPage() {
  const [sessionYear, setSessionYear] = useState('');
  const [detailsSchool, setDetailsSchool] = useState<SchoolFeesSummaryRow | null>(null);

  const { data: sessionYears = [] } = useQuery({
    queryKey: ['ho-fees-share-session-years'],
    queryFn: HoFeesShareApi.sessionYearsForOrg,
  });
  const effectiveSessionYear = sessionYear || sessionYears[0] || '';

  const { data: rows = [], isFetching } = useQuery({
    queryKey: ['org-school-fees-summary', effectiveSessionYear],
    queryFn: () => HoFeesShareApi.schoolSummaryForOrg(effectiveSessionYear),
    enabled: !!effectiveSessionYear,
  });

  // Totals are computed over the full (unfiltered, unpaginated) set.
  const totals = rows.reduce(
    (acc, r) => ({ total: acc.total + r.totalFee, paid: acc.paid + r.paidFee, due: acc.due + r.dueFee }),
    { total: 0, paid: 0, due: 0 },
  );

  // All rows arrive in one shot (bounded by org school count), so search +
  // pagination happen client-side rather than round-tripping to the server.
  const dt = useDataTable({ defaultLimit: 20 });
  const filteredRows = useMemo(
    () => (dt.params.search ? rows.filter((r) => r.schoolName.toLowerCase().includes(dt.params.search!.toLowerCase())) : rows),
    [rows, dt.params.search],
  );
  const total = filteredRows.length;
  const totalPages = Math.max(1, Math.ceil(total / dt.limit));
  const page = Math.min(dt.page, totalPages);
  const pageRows = filteredRows.slice((page - 1) * dt.limit, page * dt.limit);

  return (
    <div>
      <PageHeader title="School Summary" description="Student fee totals per school — total demanded, collected, and pending — for a session year." />

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <Select className="w-48" value={effectiveSessionYear} onChange={(e) => setSessionYear(e.target.value)}>
          {sessionYears.map((y) => (
            <option key={y} value={y}>
              {y}
            </option>
          ))}
        </Select>
        <Input className="w-64" placeholder="Search school…" value={dt.searchInput} onChange={(e) => dt.setSearchInput(e.target.value)} />
      </div>

      <div className="mb-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
        <div className="card p-4">
          <div className="text-xs font-medium uppercase tracking-wide text-slate-500">Total Fees</div>
          <div className="mt-1 text-xl font-semibold text-slate-900">{fmtMoney(totals.total)}</div>
        </div>
        <div className="card p-4">
          <div className="text-xs font-medium uppercase tracking-wide text-slate-500">Collected Fees</div>
          <div className="mt-1 text-xl font-semibold text-green-600">{fmtMoney(totals.paid)}</div>
        </div>
        <div className="card p-4">
          <div className="text-xs font-medium uppercase tracking-wide text-slate-500">Pending Fees</div>
          <div className="mt-1 text-xl font-semibold text-amber-600">{fmtMoney(totals.due)}</div>
        </div>
      </div>

      <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
        <table className="min-w-full divide-y divide-slate-200 text-sm">
          <thead className="bg-slate-50">
            <tr>
              <th className="px-4 py-2 text-left font-medium text-slate-600">No.</th>
              <th className="px-4 py-2 text-left font-medium text-slate-600">School</th>
              <th className="px-4 py-2 text-left font-medium text-slate-600">Total Fee</th>
              <th className="px-4 py-2 text-left font-medium text-slate-600">Paid Fee</th>
              <th className="px-4 py-2 text-left font-medium text-slate-600">Due Fee</th>
              <th className="px-4 py-2 text-left font-medium text-slate-600">Details</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {pageRows.map((r, i) => (
              <tr key={r.schoolId} className="hover:bg-slate-50">
                <td className="px-4 py-2 text-slate-400">{(page - 1) * dt.limit + i + 1}</td>
                <td className="px-4 py-2 font-medium text-slate-800">{r.schoolName}</td>
                <td className="px-4 py-2 text-slate-600">{fmtMoney(r.totalFee)}</td>
                <td className="px-4 py-2 text-green-600">{fmtMoney(r.paidFee)}</td>
                <td className="px-4 py-2 text-amber-600">{fmtMoney(r.dueFee)}</td>
                <td className="px-4 py-2">
                  <button type="button" className="text-sm font-medium text-blue-600 hover:underline" onClick={() => setDetailsSchool(r)}>
                    View Details
                  </button>
                </td>
              </tr>
            ))}
            {!isFetching && pageRows.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-sm text-slate-400">
                  {total === 0 && rows.length === 0 ? `No fee data for ${effectiveSessionYear || 'this session year'}.` : 'No schools match your search.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
        <DataTablePagination state={dt} result={{ items: pageRows, total, page, limit: dt.limit, totalPages }} />
      </div>

      {detailsSchool && <SchoolClassDetailsModal school={detailsSchool} sessionYear={effectiveSessionYear} onClose={() => setDetailsSchool(null)} />}
    </div>
  );
}
